import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlayerMatch } from '../../models/PlayerMatch';
import { Team } from '../../models/Team';
import TeamLogo from '../../components/TeamLogo';
import { Side, getSideStats, fmtR3, fmtSwing, legacyRating, playerName } from './matchStats';
import styles from './MatchPage2.module.css';

interface Match2ScoreboardProps {
    team: Team;
    score: number;
    rows: PlayerMatch[]; // already sorted
    side: Side;
    eco: boolean;
    hasR3: boolean; // false for pre-R3 matches: drop Swing, show Rating 2.1/2.0 instead
    // Uploaded demos use synthetic teams with no public page, so their names aren't links.
    linkTeam?: boolean;
}

// One team's overview board. With R3 data: SPILLER | K-D | Swing | ADR | KAST | Rating 3.0.
// Without it (old seasons): SPILLER | K/D/A | ADR | KAST | Rating 2.1 — Swing/eco/R3 are
// dropped entirely rather than shown as dead "–" columns. Null engine values render as "–".
const Match2Scoreboard: React.FC<Match2ScoreboardProps> = ({ team, score, rows, side, eco, hasR3, linkTeam = true }) => {
    const { t } = useTranslation('pages');
    const { t: ts } = useTranslation('stats');

    // Legacy boards prefer the "Rating 2.1" header, falling back to "Rating 2.0" when the
    // whole board only carries 2.0 (very old seasons).
    const legacyLabel = rows.some(p => getSideStats(p, side).rating21 != null)
        ? ts('rating21')
        : ts('rating2');

    const Header = () => (
        <div className={`${styles.boardCols} ${hasR3 ? '' : styles.boardCols5}`}>
            <div>{t('match2.player')}</div>
            <div className={styles.cellCenter}>{eco ? <span className={styles.ecoLabel}>{ts('ekd')}</span> : ts('kda')}</div>
            {hasR3 && <div className={styles.cellCenter}>{ts('swing')}</div>}
            <div className={styles.cellCenter}>{eco ? <span className={styles.ecoLabel}>{ts('eadr')}</span> : ts('adr')}</div>
            <div className={styles.cellCenter}>{eco ? <span className={styles.ecoLabel}>{ts('ekast')}</span> : ts('kast')}</div>
            <div className={styles.cellCenter}>{hasR3 ? ts('rating30') : legacyLabel}</div>
        </div>
    );

    return (
        <div className={styles.board}>
            <div className={styles.boardHeader}>
                <TeamLogo logo={team.teamLogo} name={team.teamName} className={styles.boardLogo} />
                {linkTeam
                    ? <Link to={`/team/${team.teamId}`} className={styles.boardTeamName}>{team.teamName}</Link>
                    : <span className={styles.boardTeamName}>{team.teamName}</span>}
                <span className={styles.boardScore}>({score})</span>
            </div>
            <Header />
            {rows.map((p, i) => {
                const s = getSideStats(p, side);
                // Raw side counts: the PlayerMatch service maps 0-valued side stats to
                // undefined (truthy rounding), so show "0/x/y" as long as any value is
                // present. Eco values stay strict — null means the engine didn't run.
                // Eco has no assist weighting, so eco mode shows eK-eD only (whole
                // numbers, like HLTV's eco columns).
                const kd = eco
                    ? (s.eKills != null && s.eDeaths != null ? `${Math.round(s.eKills)}-${Math.round(s.eDeaths)}` : '–')
                    : (s.kills != null || s.deaths != null || s.assists != null
                        ? `${Math.round(s.kills ?? 0)}/${Math.round(s.deaths ?? 0)}/${Math.round(s.assists ?? 0)}`
                        : '–');
                const adr = eco
                    ? (s.eAdr != null ? s.eAdr.toFixed(1) : '–')
                    : (s.adr != null ? s.adr.toFixed(1) : '–');
                const kast = eco
                    ? (s.eKast != null ? `${Math.round(s.eKast)}%` : '–')
                    : (s.kast != null ? `${Math.round(s.kast)}%` : '–');
                // Rating column: R3 (colored around 1.00) or the legacy 2.1/2.0 fallback.
                const legacy = legacyRating(s);
                const ratingVal = hasR3 ? s.rating30 : legacy;
                return (
                    <div key={p.steamId} className={`${styles.boardRow} ${hasR3 ? '' : styles.boardRow5} ${i % 2 === 1 ? styles.boardRowAlt : ''}`}>
                        <Link to={`/player/${p.steamId}`} className={styles.playerCell}>
                            <span className={styles.playerCellName}>{playerName(p)}</span>
                        </Link>
                        <div className={`${styles.cellCenter} ${styles.cellKd}`}>{kd}</div>
                        {hasR3 && (
                            <div className={`${styles.cellCenter} ${styles.cellSwing} ${s.swingPct != null ? (s.swingPct >= 0 ? styles.good : styles.bad) : ''}`}>
                                {fmtSwing(s.swingPct)}
                            </div>
                        )}
                        <div className={styles.cellCenter}>{adr}</div>
                        <div className={styles.cellCenter}>{kast}</div>
                        <div className={`${styles.cellCenter} ${styles.r3Cell} ${ratingVal != null ? (ratingVal >= 1.0 ? styles.good : styles.bad) : ''}`}>
                            {fmtR3(ratingVal)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Match2Scoreboard;
